import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { PanicAlert, useRealtime } from "../contexts/RealtimeContext";
import Button from "./Button";

/**
 * App-level panic alert overlay. Pops a screen-blocking modal when a
 * `panic` socket event lands; plays a short attention beep; doesn't
 * dismiss until the dispatcher hits "Acknowledge". On ack we mark the
 * underlying `panic_alert` notification as read (so it doesn't double-
 * surface in the bell), drop the alert from the local queue, and
 * navigate to the mission so the dispatcher can act.
 *
 * Multiple panics queue up — most recent first. Acknowledging one
 * surfaces the next.
 */
const RealtimePanicOverlay: React.FC = () => {
    const { panicQueue, acknowledgePanic } = useRealtime();
    const navigate = useNavigate();
    const lastSoundedIdRef = useRef<number | null>(null);

    const head: PanicAlert | undefined = panicQueue[0];

    // Play the alert tone exactly once per new panic. Browsers block
    // audio without a user gesture; if play() rejects we silently move
    // on — the visible modal is the primary affordance anyway.
    useEffect(() => {
        if (!head) return;
        const id = head.legIncidentId ?? -1;
        if (lastSoundedIdRef.current === id) return;
        lastSoundedIdRef.current = id;
        try {
            // Web Audio short two-tone beep generated inline — no asset to ship.
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const playTone = (freq: number, startSec: number, durSec: number) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = "square";
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0.001, ctx.currentTime + startSec);
                gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + startSec + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startSec + durSec);
                osc.start(ctx.currentTime + startSec);
                osc.stop(ctx.currentTime + startSec + durSec);
            };
            playTone(880, 0, 0.18);
            playTone(660, 0.22, 0.18);
            playTone(880, 0.44, 0.18);
        } catch { /* gesture-blocked or unsupported — ignore */ }
    }, [head]);

    if (!head) return null;

    const reporterName = head.reporter?.name ?? "Operator";
    const triggeredAt = head.triggeredAt ? new Date(head.triggeredAt) : new Date();
    const locText = head.location ? `${head.location.lat.toFixed(5)}, ${head.location.lng.toFixed(5)}` : "Location unknown";
    const mapsUrl = head.location
        ? `https://www.google.com/maps?q=${head.location.lat},${head.location.lng}`
        : null;

    const handleAcknowledge = async () => {
        // We don't have the underlying notificationId in the socket
        // payload (only the incidentId), so we lean on the existing
        // mark-all-as-read flow if the dispatcher prefers — otherwise
        // they can clear individual rows in the bell.
        acknowledgePanic(head.legIncidentId);
    };

    const handleOpenIncident = async () => {
        acknowledgePanic(head.legIncidentId);
        if (head.missionId) {
            navigate(`/mission/${head.missionId}`);
        }
    };

    return (
        <Modal
            show
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            contentClassName="border-0"
        >
            <div
                style={{
                    backgroundColor: "#7a1212",
                    color: "#ffffff",
                    border: "3px solid #ff3b3b",
                    borderRadius: 6,
                    padding: 24,
                    boxShadow: "0 0 32px rgba(255, 59, 59, 0.6)",
                    fontFamily: "inherit",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div
                        style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: "#ff3b3b",
                            boxShadow: "0 0 12px #ff3b3b",
                        }}
                    />
                    <div style={{ fontSize: "var(--fs-2xl)", fontWeight: 800, letterSpacing: 2 }}>
                        PANIC ALERT
                    </div>
                    <div style={{ flex: 1 }} />
                    {panicQueue.length > 1 ? (
                        <div
                            style={{
                                padding: "4px 10px",
                                border: "1px solid #ffffff",
                                borderRadius: 2,
                                fontSize: "var(--fs-2xs)",
                                fontWeight: 800,
                                letterSpacing: 1.4,
                            }}
                        >
                            {panicQueue.length} ACTIVE
                        </div>
                    ) : null}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
                    {head.reporter?.photoUrl ? (
                        <img
                            src={head.reporter.photoUrl}
                            alt={reporterName}
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                border: "2px solid #ffffff",
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                backgroundColor: "#ffffff",
                                color: "#7a1212",
                                fontWeight: 800,
                                fontSize: "var(--fs-2xl)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px solid #ffffff",
                            }}
                        >
                            {reporterName.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "?"}
                        </div>
                    )}
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "var(--fs-xl)", fontWeight: 700 }}>{reporterName}</div>
                        {head.missionRef ? (
                            <div style={{ fontFamily: "Courier New, monospace", fontSize: "var(--fs-sm)", opacity: 0.85, letterSpacing: 1 }}>
                                {head.missionRef}
                            </div>
                        ) : null}
                        <div style={{ fontSize: "var(--fs-xs)", opacity: 0.7, marginTop: 4 }}>
                            Triggered {triggeredAt.toLocaleTimeString()}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        backgroundColor: "rgba(0,0,0,0.3)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 4,
                        padding: 12,
                        marginBottom: 16,
                    }}
                >
                    <div style={{ fontSize: "var(--fs-2xs)", fontWeight: 800, letterSpacing: 1.4, opacity: 0.8 }}>
                        LAST KNOWN LOCATION
                    </div>
                    <div style={{ fontFamily: "Courier New, monospace", fontSize: "var(--fs-md)", marginTop: 4 }}>
                        {locText}
                    </div>
                    {mapsUrl ? (
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: "var(--fs-2xs)", color: "#ffd54a", textDecoration: "underline", display: "inline-block", marginTop: 4 }}
                        >
                            Open in Google Maps →
                        </a>
                    ) : null}
                </div>

                {head.message ? (
                    <div
                        style={{
                            backgroundColor: "rgba(0,0,0,0.3)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: 4,
                            padding: 12,
                            marginBottom: 20,
                            fontSize: "var(--fs-sm)",
                            lineHeight: 1.5,
                        }}
                    >
                        <div style={{ fontSize: "var(--fs-2xs)", fontWeight: 800, letterSpacing: 1.4, opacity: 0.8, marginBottom: 4 }}>
                            MESSAGE
                        </div>
                        {head.message}
                    </div>
                ) : null}

                <div style={{ display: "flex", gap: 12 }}>
                    <Button
                        onClick={handleAcknowledge}
                        style={{ flex: 1, fontWeight: 700, letterSpacing: 1 }}
                    >
                        ACKNOWLEDGE
                    </Button>
                    {head.missionId ? (
                        <Button
                            variant="primary"
                            onClick={handleOpenIncident}
                            style={{ flex: 1, fontWeight: 700, letterSpacing: 1 }}
                        >
                            OPEN MISSION →
                        </Button>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

export default RealtimePanicOverlay;
